<template>
    <div class="plan-group-chart" :class="{ fullscreen: fullScreen }">
        <BarChart :options="getOptions()" :colors="['#cb5d0f', '#4213ce', '#c9d906']" class="bar-chart" />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard title="Total Package" :value="stats.totalPackage" unit="site" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import BarChart from "../../Pages/Chatbot/pages/reports/charts/BarChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { ADMIN_REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";

const log = log2("PlanGroupChart");
export default Vue.extend({
    name: "PlanGroupChart",
    components: { ChartDoddleIcon, TableView, IconButton, StatCard, BarChart },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            stats: {
                totalPackage: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            data: [],
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".plan-group-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            ADMIN_REPORT_API.usePlanGroup(this.filters)
                .then((res) => {
                    log("usePlanGroup data fetched", res);
                    this.data = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getCols() {
            return [
                {
                    key: "paymentMethod",
                    field: "paymentMethod",
                    title: "Ödeme Yöntemi",
                },
                {
                    key: "siteCount",
                    field: "siteCount",
                    title: this.$t("report.siteCount"),
                },
            ];
        },
        getRows() {
            const rows = this.data.map((item) => {
                return [item.paymentMethod, item.siteCount];
            });
            return rows;
        },
        getStats() {
            return {
                totalPackage: this.data.map((x) => x.count).reduce((a, b) => a + b, 0),
            };
        },
        getOptions() {
            const data = this.data.map((x) => {
                return [x.title, x.count];
            });
            return {
                dataset: [
                    {
                        dimensions: ["title", "count"],
                        source: data,
                    },
                    {
                        transform: {
                            type: "sort",
                            config: { dimension: "count", order: "desc" },
                        },
                    },
                ],
                xAxis: {
                    type: "category",
                    axisLabel: { interval: 0, rotate: 30 },
                },
                yAxis: {},
                series: {
                    type: "bar",
                    encode: { x: "title", y: "count" },
                    datasetIndex: 1,
                },
            };
        },
        exportToExcel() {
            const data = this.data.map((item) => {
                return [item.title, item.count];
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    title: "Toplam paket sayısı",
                    count: stats.totalPackage,
                },
            ];
            expertToExcel(excelData, `plans.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".plan-group-chart"),
            name: "wave",
        });
        this.stats = this.getStats();
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.plan-group-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.plan-group-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .bar-chart {
        min-height: 400px;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
